import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import accessPointInstallatieImg from "@/assets/access-point-installatie.png.asset.json";
import { Wifi } from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

const FAQS = [
  {
    q: "Wat is het verschil tussen een access point en een wifi-extender?",
    a: "Een access point (AP) maakt directe verbinding met je router of switch via een netwerkkabel en creëert een nieuw wifi-netwerk. Dit geeft de beste prestaties en stabiliteit. Een wifi-extender (repeater) vangt het bestaande wifi-signaal op en herhaalt dit, wat leidt tot halvering van de bandbreedte en meer latency. Voor professioneel gebruik adviseren wij altijd bedrade access points.",
  },
  {
    q: "Hoeveel access points heb ik nodig?",
    a: "Reken voor een gemiddelde woning op 1 AP per 80–100 m² per verdieping. In kantoren met veel gelijktijdige apparaten of dikke wanden plaats je vaker een AP per ruimte. Met een site survey bepalen we het exacte aantal en de optimale plaatsing.",
  },
  {
    q: "Welke wifi-standaard heb ik nodig: wifi 5, 6 of 6E?",
    a: "Wifi 6 (802.11ax) is de huidige standaard: hogere snelheden, lagere latency en betere prestaties bij veel apparaten tegelijk. Wifi 6E voegt de 6 GHz-band toe — interessant als je apparaten dit ondersteunen en je dichtbij het AP zit. Wifi 5 volstaat alleen nog voor lichte thuissituaties.",
  },
  {
    q: "Wat is een mesh wifi-systeem?",
    a: "Een mesh bestaat uit meerdere access points die samen één groot, naadloos wifi-netwerk vormen. Je apparaten roamen automatisch naar het sterkste AP. Ideaal voor grote woningen, meerdere verdiepingen of gebouwen met dikke muren.",
  },
  {
    q: "Heb ik PoE nodig voor mijn access points?",
    a: "Vrijwel alle professionele access points worden gevoed via PoE (Power over Ethernet). Eén UTP-kabel levert dan zowel data als stroom. Dit bespaart een stopcontact bij elk AP en maakt installatie aan plafond of wand eenvoudig.",
  },
];

const BENEFITS = [
  "Wifi 6 / Wifi 6E voor hoge snelheden",
  "PoE voeding — één kabel voor data en stroom",
  "Seamless roaming tussen access points",
  "Centraal beheer via UniFi controller",
  "Ondersteuning voor VLAN en gast-wifi",
];

const APPLICATIONS = [
  "Woningen met meerdere verdiepingen",
  "Kantoren en bedrijfspanden",
  "Horeca, retail en hotels",
  "Magazijnen en buitenruimtes",
];

export const Route = createFileRoute("/access-points")({
  head: () => {
    const url = `${SITE_URL}/access-points`;
    const title = "Access Points Wifi 6 & UniFi | Elvion Group Nederland helpt u graag verder";
    const description =
      "Elvion Group Nederland helpt u graag verder met professionele access points voor razendsnelle wifi: Wifi 6, PoE en centraal beheer met Ubiquiti UniFi — advies, levering en installatie.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: AccessPointsPage,
});

function AccessPointsPage() {
  return (
    <>
      <Header />
      <ContactCta
        imageUrl={accessPointInstallatieImg.url}
        imageAlt="Monteur installeert access point aan het plafond in een kantoor"
        title="Overal stabiele wifi nodig?"
        description="Onze netwerkspecialisten ontwerpen en installeren een dekkend access point-netwerk voor je woning, kantoor of bedrijfspand. Snel, schaalbaar en zonder dode hoeken."
        bullets={[
          "Wifi 6 access points van Ubiquiti UniFi",
          "Site survey en advies op maat",
          "Professionele installatie en centraal beheer",
        ]}
        primaryCta={{ label: "Vraag advies aan", to: "/klantenservice" }}
        secondaryCta={{ label: "Offerte opvragen", to: "/offerte-aanvragen" }}
      />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">


        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            UniFi access points: overal razendsnelle wifi — Elvion Group Nederland
          </h1>
          <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
            Een access point (AP) zorgt voor stabiele, snelle wifi door direct
            bedraad op je netwerk aan te sluiten. Wij leveren professionele
            Wifi 6 access points van onder andere Ubiquiti UniFi — schaalbaar
            van een enkele AP tot complete mesh-netwerken met centraal beheer.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-stretch gap-3">
          <div className="hidden sm:flex aspect-square items-center justify-center rounded-xl bg-orange/10 text-orange self-stretch px-3">
            <Wifi className="size-6" />
          </div>
          <Link
            to="/c"
            className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
          >
            Bekijk access points
          </Link>
          <Link
            to="/internet-calculator"
            className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
          >
            Internet calculator
          </Link>
          <Link
            to="/klantenservice"
            className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-card"
          >
            Vraag advies
          </Link>
        </div>

        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <article>
            <h2 className="text-xl font-bold tracking-tight">
              Wat is een access point?
            </h2>
            <p className="mt-2 text-text-secondary">
              Een access point is een bedraad apparaat dat een wifi-netwerk
              uitzendt. In tegenstelling tot een wifi-extender deelt een AP de
              bandbreedte niet — je krijgt op elke locatie de volle snelheid van
              je internetverbinding. Meerdere AP's vormen samen één naadloos
              netwerk waar apparaten automatisch tussen roamen.
            </p>
          </article>
          <article>
            <h2 className="text-xl font-bold tracking-tight">
              Waarom Ubiquiti UniFi?
            </h2>
            <p className="mt-2 text-text-secondary">
              UniFi access points combineren topprestaties met een gratis
              centraal dashboard. Je beheert alle AP's, gast-wifi en VLAN's
              vanuit één scherm. Ideaal voor kantoren, horeca en grotere
              woningen waar betrouwbare wifi cruciaal is.
            </p>
          </article>
          <article>
            <h2 className="text-xl font-bold tracking-tight">
              Wifi 6 en Wifi 6E
            </h2>
            <p className="mt-2 text-text-secondary">
              Wifi 6 (802.11ax) levert tot 9,6 Gbps theoretische snelheid en
              gaat beter om met veel gelijktijdige apparaten. Wifi 6E voegt de
              6 GHz-band toe voor extra capaciteit en minder storing — perfect
              voor moderne smart homes en hybride kantoren.
            </p>
          </article>
          <article>
            <h2 className="text-xl font-bold tracking-tight">
              PoE: één kabel is genoeg
            </h2>
            <p className="mt-2 text-text-secondary">
              Alle access points die wij leveren werken op Power over Ethernet.
              Je hoeft dus geen stopcontact bij het AP te plaatsen — één
              UTP-kabel naar een PoE-switch is voldoende voor zowel data als
              stroom.
            </p>
          </article>
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">Voordelen</h2>
            <ul className="mt-3 space-y-2">
              {BENEFITS.map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Toepassingen</h2>
            <ul className="mt-3 space-y-2">
              {APPLICATIONS.map((a) => (
                <li
                  key={a}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="cursor-pointer list-none font-semibold text-foreground">
                  {f.q}
                </summary>
                <p className="mt-2 text-text-secondary">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Direct bestellen of advies?
          </h2>
          <p className="mt-2 text-text-secondary">
            Niet zeker welk access point past bij jouw situatie? Onze adviseurs
            helpen je met een passend voorstel — van losse AP tot complete
            UniFi-setup met switch en controller.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/c"
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Naar de webshop
            </Link>
            <Link
              to="/klantenservice"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-card"
            >
              Contact opnemen
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
